<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
    import { hasNextPage } from "@/utils/paginationUtils/paginationUtils";
    import SimpleState from "@/utils/SimpleState";
    import ReviewsEmptyList from "@apps/reviews/components/ReviewsEmptyListState.svelte";
    import ReviewsList from "@apps/reviews/components/ReviewsList/ListingLayout/ReviewsList.svelte";
    import ReviewsMasonryList from "@apps/reviews/components/ReviewsList/ReviewsMasonryLayout/ReviewsMasonryList.svelte";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import {
        REVIEWS_FILTERS,
        REVIEWS_WIDGET_LAYOUT,
        REVIEW_TYPE,
        SCROLL_REVIEWS_TAB,
    } from "@apps/reviews/constants";
    import ReviewsQueryBase from "@apps/reviews/queries/ReviewsQueryBase";
    import ReviewsTranslationsQuery from "@apps/reviews/queries/ReviewsTranslationsQuery";
    import ReviewsServiceBase from "@apps/reviews/services/ReviewsServiceBase";
    import type { GetReviewsWithFiltersParamsDTO } from "@apps/reviews/services/ReviewsServiceBase";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import ReviewsTranslationsService from "@apps/reviews/services/ReviewsTranslationsService";
    import {
        reviewsEditFormModalToken,
        reviewsFormModalToken,
    } from "@apps/reviews/tokens";
    import { SortingManager } from "@apps/reviews/utils/SortingManager";
    import FormManager from "@lib/FormManager/FormManager";
    import { ValidationStrategy } from "@lib/FormManager/types";
    import yupResolver from "@lib/FormManager/yupResolver";
    import { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import ScrollManager from "@lib/ScrollManager/ScrollManager";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import Observable from "@ui-utils/Observable.svelte";
    import { lastValueFrom, map } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";
    import { object } from "yup";

    import ActionItem from "@components/Actions/ActionItem.svelte";
    import ActionsList from "@components/Actions/ActionsList.svelte";
    import ActionsPopover from "@components/Actions/ActionsPopover.svelte";
    import DropdownWithValueActivatorButton from "@components/Actions/DropdownWithValueActivatorButton.svelte";
    import Button from "@components/Buttons/Button.svelte";
    import Icon from "@components/Icon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import FilterIcon from "@components/icons/FilterIcon.svelte";
    import LoadMore from "@components/LoadMore.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import T from "@components/T.svelte";

    import ReviewsFilter from "../../components/ReviewsFilter.svelte";
    import ReviewsFilterQuery from "../../queries/ReviewsFilterQuery";
    import ReviewsFilterService from "../../services/ReviewsFilterService";
    import { LEAVE_REVIEW_SOURCE } from "../review_form_modal_widget/constants";
    import ReviewsGalleryManager from "../reviews_gallery_widget/ReviewsGalleryManager";
    import ReviewsImagesSliderService from "../reviews_product_page_widget/services/ReviewsImagesSliderService";
    import { serializeFilters } from "./helpers/serializeFilters";

    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let productIdManager: ProductIdManagerInterface;
    export let reviewsPerPage: number;
    export let reviewsService: ReviewsServiceBase;
    export let reviewsQuery: ReviewsQueryBase;
    export let reviewsTranslationsService: ReviewsTranslationsService;
    export let reviewsTranslationsQuery: ReviewsTranslationsQuery;
    export let localizationInfoProvider: LocalizationInfoProviderInterface;
    export let reviewsGalleryManager: ReviewsGalleryManager;
    export let modalManager: ModalManager;
    export let reviewsFilterQuery: ReviewsFilterQuery;
    export let reviewsFilterService: ReviewsFilterService;
    export let reviewsImagesSliderService:
        | ReviewsImagesSliderService
        | undefined;
    export let sortingManager: SortingManager;
    export let showReviewsFilterUIState: SimpleState<boolean>;
    export let showAddReviewButton: boolean;
    export let reviewsDefaultFilters: string | undefined;
    export let isNestedWidget: boolean;
    export let showAddReviewButtonInEmptyList: boolean;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let widgetName: string;
    export let afterWidgetInject: () => void;

    const reviewsSettings$ =
        reviewsStorefrontValuesService.selectReviewsSettings();
    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    const scrollManager = container.resolve(ScrollManager);
    let widgetRef: HTMLElement;

    const productId$ = productIdManager.productId$;

    const reviews$ = reviewsQuery.all$;
    const reviewsStatus$ = reviewsQuery.status$;
    const reviewsPaginationMeta$ = reviewsQuery.paginationMeta$;

    const reviewsTranslations$ = reviewsTranslationsQuery.translations$;
    const translationsPendingIds$ = reviewsTranslationsQuery.pendingIds$;
    const isLocaleSupported$ = reviewsTranslationsQuery.isLocaleSupported$;

    const showReviewsFilter$ = showReviewsFilterUIState.selectState();
    const reviewsFilter$ = reviewsFilterQuery.reviewsFilter$;
    const i18n = container.resolve(I18nModule).exports.i18n;
    const reviewsFiltersCount$ = reviewsFilter$.pipe(
        map((filters) => {
            return (
                filters.data.customerFilters.length +
                filters.data.productFilters.length
            );
        })
    );

    const isAuthenticated$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    // FormManager
    const filterFormManager = new FormManager({
        resolver: yupResolver(object({})),
        initialValues: {},
        validationStrategy: ValidationStrategy.OnSubmit,
        unregisterOnUnmount: true,
        onSubmit: ({ values, errors }) => {
            if (errors || !values) return;
        },
    });
    let selectedFilter$ = filterFormManager.selectValues();

    // Sorting
    const currentSortValue$ = sortingManager.selectCurrentSortValue();
    const currentSortItem$ = sortingManager.selectCurrentSortItem();
    const sortItems$ = sortingManager.selectSortItems();

    let sortingPopoverIsOpen = false;
    const handleOpenSortingPopover = () => {
        sortingPopoverIsOpen = true;
    };
    const handleCloseSortingPopover = () => {
        sortingPopoverIsOpen = false;
    };

    const buildGetReviewsParams = (offset: number) => {
        if ($productId$ === null) {
            throw new Error("Product ID is not defined");
        }

        const filters = serializeFilters($selectedFilter$);

        const params: GetReviewsWithFiltersParamsDTO = {
            productId: $productId$,
            sortingOptions: [$currentSortValue$],
            offset: offset,
            perPage: reviewsPerPage,
            widget: widgetName,
            ...filters,
        };

        const reviewsDefaultFiltersArr = reviewsDefaultFilters?.includes(",")
            ? reviewsDefaultFilters.split(",")
            : [reviewsDefaultFilters];

        if (
            reviewsDefaultFiltersArr.includes(REVIEWS_FILTERS.ONLY_WITH_PHOTOS)
        ) {
            params.onlyWithPhotos = true;
        }

        if (
            reviewsDefaultFiltersArr.includes(
                REVIEWS_FILTERS.ONLY_PINNED_REVIEWS
            )
        ) {
            params.onlyPinnedReviews = true;
        }

        if (
            reviewsDefaultFiltersArr.includes(REVIEWS_FILTERS.ONLY_SHOP_REVIEWS)
        ) {
            params.onlyShopReviews = true;
        }

        if (
            reviewsDefaultFiltersArr.includes(
                REVIEWS_FILTERS.ONLY_PRODUCT_REVIEWS
            )
        ) {
            params.onlyProductReviews = true;
        }

        return params;
    };

    // Reviews getting
    const getReviews = () => {
        if (!$reviewsSettings$) return;

        if ($productId$ === null) {
            throw new Error("Product ID is not defined");
        }

        lastValueFrom(reviewsService.getReviews(buildGetReviewsParams(0)));
    };

    $: {
        $reviewsSettings$;
        $currentSortValue$;
        $selectedFilter$;
        getReviews();
    }

    const handleReviewsLoadMore = () => {
        if ($productId$ === null) {
            throw new Error("Product ID is not defined");
        }

        lastValueFrom(
            reviewsService.loadMoreReviews(
                buildGetReviewsParams(
                    $reviewsPaginationMeta$.perPage +
                        $reviewsPaginationMeta$.currentOffset
                )
            )
        );
    };

    const checkIfLocaleSupported = () => {
        if ($reviewsSettings$) {
            if (
                $reviewsSettings$.canUseTranslation &&
                $reviewsSettings$.isReviewsTranslationEnabled
            ) {
                lastValueFrom(
                    reviewsTranslationsService.checkIfLocaleSupported({
                        locale: localizationInfoProvider.getLanguageIsoCode(),
                    })
                );
            }
        }
    };

    $: {
        $reviewsSettings$;
        checkIfLocaleSupported();
    }

    const getFilters = () => {
        if ($productId$) {
            lastValueFrom(
                reviewsFilterService.getAttributeFilters({
                    productId: $productId$,
                })
            );
        }
    };

    $: {
        $productId$;
        getFilters();
    }

    const handleAddReview = () => {
        modalManager.openModal(reviewsFormModalToken, undefined, {
            productId: $productId$ || undefined,
            source: LEAVE_REVIEW_SOURCE.REVIEWS_PRODUCT_PAGE_WIDGET,
            reviewType: REVIEW_TYPE.PRODUCT_REVIEW,
            onLeaveReviewCb: (review) => {
                reviewsService.appendReview(review);
            },
        });
    };

    const handleOpenReview = (
        event: CustomEvent<{ reviewUid: string; activeImageIndex: number }>
    ) => {
        reviewsGalleryManager.openReviewModal({
            reviews$,
            activeReviewUid: event.detail.reviewUid,
            activeImageIndex: event.detail.activeImageIndex,
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_PRODUCT_PAGE_WIDGET,
        });
    };

    const handleSeeTranslation = (
        event: CustomEvent<{ reviewId: number; targetLanguage: string }>
    ) => {
        lastValueFrom(
            reviewsTranslationsService.seeTranslation({
                reviewId: event.detail.reviewId,
                targetLanguage: event.detail.targetLanguage,
            })
        );
    };

    const handleOpenFilter = () => {
        showReviewsFilterUIState.setState(!$showReviewsFilter$);
    };

    const handleEditReview = async (
        event: CustomEvent<{ reviewId: number }>
    ) => {
        const reviewId = event.detail.reviewId;

        if (reviewId && $productId$ && $reviews$) {
            setTimeout(() => {
                modalManager.openModal(reviewsEditFormModalToken, undefined, {
                    productId: $productId$ as number,
                    reviews: $reviews$,
                    reviewId: reviewId,
                    onSubmit: (review) => {
                        reviewsImagesSliderService?.updateImageSlider(review);
                        reviewsService.updateReview(review);
                    },
                });
            }, 0);
        }
    };

    onMount(() => {
        if (!isNestedWidget) {
            scrollManager.register(SCROLL_REVIEWS_TAB, widgetRef);
        }
        afterWidgetInject();
    });
</script>

<div bind:this={widgetRef} class="gw-rv-reviews-list-widget">
    {#if $reviewsSettings$ && $reviewsBranding$}
        <ReviewsTheme branding={$reviewsBranding$}>
            <div class="gw-rv-reviews-list-header">
                <div class="gw-rv-reviews-list-header__left">
                    {#if $reviewsFiltersCount$ > 0}
                        <Button
                            class="gw-rv-listing-layout__filter{$showReviewsFilter$
                                ? '-active'
                                : ''}"
                            variant={$showReviewsFilter$
                                ? "contained"
                                : "opacityOnHover"}
                            size="large"
                            disabled={$reviews$.length === 0}
                            on:click={handleOpenFilter}
                        >
                            <Icon class="gw-rv-listing-layout__filter-icon">
                                {#if $showReviewsFilter$}
                                    <CloseIcon />
                                {:else}
                                    <FilterIcon />
                                {/if}
                            </Icon>
                            <span>
                                <T
                                    namespace="reviews.critical.ReviewsProductPageWidget"
                                    translationKey="filter_by"
                                />
                            </span>
                        </Button>
                    {/if}

                    <ActionsPopover
                        opened={sortingPopoverIsOpen}
                        disabled={$reviews$.length === 0}
                        on:open={handleOpenSortingPopover}
                        on:close={handleCloseSortingPopover}
                    >
                        <div slot="activator">
                            <Observable
                                observable={i18n.selectT(
                                    $currentSortItem$.labelTranslationNamespace,
                                    $currentSortItem$.labelTranslationKey
                                )}
                                let:value={t_sorting_name}
                            >
                                <Observable
                                    observable={i18n.selectT(
                                        "reviews.critical.ReviewsProductPageWidget",
                                        "sort_by"
                                    )}
                                    let:value={t_sort_by_label}
                                >
                                    <DropdownWithValueActivatorButton
                                        currentValue={t_sorting_name}
                                        prefix={t_sort_by_label}
                                        isOpen={sortingPopoverIsOpen}
                                        disabled={$reviews$.length === 0}
                                    />
                                </Observable>
                            </Observable>
                        </div>
                        <ActionsList>
                            {#each $sortItems$ as item}
                                <ActionItem
                                    on:click={() => {
                                        sortingManager.setCurrentSort(
                                            item.value
                                        );
                                        handleCloseSortingPopover();
                                    }}
                                >
                                    <span class="gw-sort__item-text">
                                        <T
                                            namespace={item.labelTranslationNamespace}
                                            translationKey={item.labelTranslationKey}
                                        />
                                    </span>
                                </ActionItem>
                            {/each}
                        </ActionsList>
                    </ActionsPopover>
                </div>
                <div class="gw-rv-reviews-list-header__right">
                    {#if showAddReviewButton}
                        <Button
                            variant={"opacityOnHover"}
                            size="large"
                            on:click={handleAddReview}
                        >
                            <span>
                                <T
                                    namespace="reviews.critical.ReviewsProductPageWidget"
                                    translationKey="write_a_review"
                                />
                            </span>
                        </Button>
                    {/if}
                </div>
            </div>

            <div class="gw-rv-listing-layout__reviews-list">
                {#if $showReviewsFilter$}
                    <div class="gw-rv-reviews-list-filter">
                        <ReviewsFilter
                            {filterFormManager}
                            {reviewsFilterQuery}
                        />
                    </div>
                {/if}

                {#if $reviewsStatus$ === STATUS.READY && !$reviews$.length}
                    <Observable
                        observable={i18n.selectT(
                            "reviews.ReviewsProductPageWidget",
                            "no_reviews_message"
                        )}
                        let:value={t_no_reviews_message}
                    >
                        <Observable
                            observable={i18n.selectT(
                                "reviews.critical.ReviewsProductPageWidget",
                                "write_a_review"
                            )}
                            let:value={t_add_review}
                        >
                            <ReviewsEmptyList
                                title={t_no_reviews_message}
                                actionText={showAddReviewButtonInEmptyList
                                    ? t_add_review
                                    : undefined}
                                on:action={handleAddReview}
                            />
                        </Observable>
                    </Observable>
                {:else if $reviewsBranding$.reviewsWidgetsSettings?.reviewsWidgetLayout === REVIEWS_WIDGET_LAYOUT.GRID_LAYOUT}
                    <div class="gw-rv-masonry-layout__reviews-list">
                        <ReviewsMasonryList
                            namespace="reviews.ReviewsProductPageWidget"
                            reviews={$reviews$}
                            showTitle={$reviewsSettings$.isReviewTitleEnabled}
                            status={$reviewsStatus$}
                            showProducts={false}
                            votesEnabled={$reviewsSettings$.isReviewsVotingEnabled}
                            isIncentivizedEnabled={$reviewsSettings$.isReviewsIncentivizedLabelEnabled}
                            isReviewEditingEnabled={$reviewsSettings$.isReviewEditingEnabled &&
                                $isAuthenticated$}
                            on:openReview={handleOpenReview}
                            on:editReview={handleEditReview}
                        />
                    </div>
                {:else}
                    <ReviewsList
                        namespace="reviews.ReviewsProductPageWidget"
                        reviews={$reviews$}
                        showTitle={$reviewsSettings$.isReviewTitleEnabled}
                        status={$reviewsStatus$}
                        showProducts={$reviewsSettings$.isUniteReviewsOriginProductEnabled}
                        votesEnabled={$reviewsSettings$.isReviewsVotingEnabled}
                        isIncentivizedEnabled={$reviewsSettings$.isReviewsIncentivizedLabelEnabled}
                        on:openReview={handleOpenReview}
                        translationEnabled={$reviewsSettings$.canUseTranslation &&
                            $isLocaleSupported$}
                        reviewsTranslations={$reviewsTranslations$}
                        translationsPendingIds={$translationsPendingIds$}
                        currentProductId={$productId$}
                        isReviewEditingEnabled={$reviewsSettings$.isReviewEditingEnabled &&
                            $isAuthenticated$}
                        on:seeTranslation={handleSeeTranslation}
                        on:editReview={handleEditReview}
                    />
                {/if}
                {#if hasNextPage($reviewsPaginationMeta$)}
                    <div class="gw-rv-listing-layout__load-more-wrapper">
                        <Observable
                            observable={i18n.selectT(
                                "reviews.ReviewsProductPageWidget",
                                "load_more"
                            )}
                            let:value={t_load_more}
                        >
                            <Observable
                                observable={i18n.selectT(
                                    "reviews.ReviewsProductPageWidget",
                                    "load_more_aria_label"
                                )}
                                let:value={t_load_more_aria_label}
                            >
                                <LoadMore
                                    variant="opacityOnHover"
                                    text={t_load_more}
                                    ariaLabel={t_load_more_aria_label}
                                    loading={$reviewsStatus$ === STATUS.LOADING}
                                    on:loadMore={handleReviewsLoadMore}
                                />
                            </Observable>
                        </Observable>
                    </div>
                {/if}
            </div>
        </ReviewsTheme>
    {/if}
</div>

<style lang="postcss" global>
    @import "../../../../themes/default/default.css";
    @import "../../../../themes/globals.css";

    .gw-rv-reviews-list-header {
        position: relative;
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px;
        align-items: center;
        overflow: visible;

        &__left {
            align-items: center;
            display: flex;
            gap: 5px;
        }
        &__right {
            align-items: center;
            display: flex;
            gap: 5px;
        }
    }
    .gw-rv-reviews-list-filter {
        margin-bottom: 20px;
    }

    .gw-rv-listing-layout {
        &__load-more-wrapper {
            margin-top: 1em;
        }

        &__filter-active {
            --gw-button__text-color: var(--gw-rv-theme-secondaryColor);
        }
    }
</style>
